import type { FrameSchedule } from "../animation/frameSchedule";
import type { VideoCodecId, VideoEncodingSettings } from "./videoEncoding";

export type VideoContainer = "mp4" | "webm";

export type VideoExportCapability = {
  container: VideoContainer;
  codec: VideoCodecId;
  supported: boolean;
  reason: string | null;
};

export function codecForContainer(container: VideoContainer): VideoCodecId {
  return container === "mp4" ? "avc1.42001f" : "vp09.00.10.08";
}

export async function detectVideoExportCapability(
  container: VideoContainer,
  dimensions: { width: number; height: number },
  schedule: FrameSchedule,
  bitrate: number,
): Promise<VideoExportCapability> {
  const codec = codecForContainer(container);

  if (!("VideoEncoder" in globalThis)) {
    return {
      container,
      codec,
      supported: false,
      reason: "WebCodecs video encoding is unavailable in this browser.",
    };
  }

  const settings: VideoEncodingSettings = {
    codec,
    width: dimensions.width,
    height: dimensions.height,
    bitrate,
    schedule,
    hardwareAcceleration: "prefer-hardware",
  };

  try {
    const support = await VideoEncoder.isConfigSupported({
      codec: settings.codec,
      width: settings.width,
      height: settings.height,
      bitrate: settings.bitrate,
      framerate: settings.schedule.fps,
      hardwareAcceleration: settings.hardwareAcceleration,
      latencyMode: "quality",
    });

    return {
      container,
      codec,
      supported: support.supported === true,
      reason: support.supported === true
        ? null
        : `${container.toUpperCase()} export is not supported for this resolution and frame rate on this browser/device.`,
    };
  } catch {
    return {
      container,
      codec,
      supported: false,
      reason: `${container.toUpperCase()} export capability could not be verified on this browser/device.`,
    };
  }
}
